<!-- Base.Pagination.vue (client-lms) -->
<template>
  <div class="lms_pagination">
    <button
      type="button"
      class="lms_page_btn lms_page_first"
      :class="{ disabled: currentPage === 1 }"
      @click="setPage(1)"
      title="처음페이지"
    >
      <i class="mdi mdi-chevron-double-left"></i>
    </button>

    <button
      type="button"
      class="lms_page_btn lms_page_prev"
      :class="{ disabled: currentPage === 1 }"
      @click="prevPage"
      title="이전페이지"
    >
      <i class="mdi mdi-chevron-left"></i>
    </button>

    <button
      v-for="num in pageNumbers"
      :key="num"
      type="button"
      class="lms_page_num"
      :class="{ active: currentPage === num }"
      @click="setPage(num)"
    >
      {{ num }}
    </button>

    <button
      type="button"
      class="lms_page_btn lms_page_next"
      :class="{ disabled: currentPage === totalPages }"
      @click="nextPage"
      title="다음페이지"
    >
      <i class="mdi mdi-chevron-right"></i>
    </button>

    <button
      type="button"
      class="lms_page_btn lms_page_last"
      :class="{ disabled: currentPage === totalPages }"
      @click="setPage(totalPages)"
      title="마지막페이지"
    >
      <i class="mdi mdi-chevron-double-right"></i>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";

const emit = defineEmits<{
  (e: "change", page: number): void;
}>();

const props = defineProps({
  totalCount: { type: Number, default: 0 },
  rowsPerPage: { type: Number, default: 10 },
  page: { type: Number, default: 1 },
});

const currentPage = ref(props.page);

const totalPages = computed(() => {
  return Math.max(1, Math.ceil(props.totalCount / props.rowsPerPage));
});

const pageNumbers = computed(() => {
  const numbers: number[] = [];
  const maxDisplay = 5;

  if (totalPages.value <= maxDisplay) {
    for (let i = 1; i <= totalPages.value; i++) numbers.push(i);
  } else {
    const start = Math.floor((currentPage.value - 1) / maxDisplay) * maxDisplay + 1;
    const end = Math.min(start + maxDisplay - 1, totalPages.value);
    for (let i = start; i <= end; i++) numbers.push(i);
  }

  return numbers;
});

const setPage = (page: number) => {
  if (currentPage.value === page || page < 1 || page > totalPages.value) return;
  currentPage.value = page;
  emit("change", page);
};

const prevPage = () => {
  if (currentPage.value > 1) setPage(currentPage.value - 1);
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) setPage(currentPage.value + 1);
};

watch(() => props.page, (val) => {
  currentPage.value = val;
});
</script>

<style scoped>
.lms_pagination {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.lms_page_btn,
.lms_page_num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 50%;
  transition: background-color 0.15s, color 0.15s;
  color: var(--lms-on-surface-medium-var, rgba(0, 0, 0, 0.5));
}

.lms_page_btn:hover:not(.disabled),
.lms_page_num:hover:not(.active) {
  background-color: rgba(0, 0, 0, 0.04);
}

.lms_page_btn.disabled {
  color: var(--lms-on-surface-low-var, rgba(0, 0, 0, 0.18));
  cursor: default;
}

.lms_page_num {
  font-family: "Pretendard", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: var(--lms-on-surface-medium-var, rgba(0, 0, 0, 0.5));
}

.lms_page_num.active {
  background-color: var(--lms-primary-300);
  color: #FFFFFF;
  font-weight: 600;
  border: none;
}

/* .lms_page_num.active:hover {
  background-color: #4A62E4;
} */

.lms_page_btn .mdi {
  font-size: 24px;
}

@media (max-width: 768px) {
  .lms_page_btn,
  .lms_page_num {
    width: 24px;
    height: 24px;
  }

  .lms_page_btn .mdi {
    font-size: 20px;
  }
}
</style>
